import { useGetConnectorManifestQuery } from '@/core/api/connector'

export type ConnectorPlatform = 'windows' | 'macos' | 'unsupported'

/**
 * Best-effort detection of the visitor's desktop OS. The connector only ships
 * for Windows and macOS; anything else gets no download.
 */
export function detectConnectorPlatform(): ConnectorPlatform {
  if (typeof navigator === 'undefined') return 'unsupported'

  const hint = `${navigator.platform ?? ''} ${navigator.userAgent ?? ''}`.toLowerCase()
  if (hint.includes('win')) return 'windows'
  if (hint.includes('mac')) return 'macos'

  return 'unsupported'
}

export type ConnectorDownload = {
  platform: ConnectorPlatform
  /** Download URL for this OS, or undefined when that platform has no release yet. */
  url?: string
  /** Secondary macOS `.zip`, for sites where the `.pkg` is blocked. */
  zipUrl?: string
  version?: string
  isLoading: boolean
  /** True when the manifest loaded but carries no package for this OS. */
  isUnavailable: boolean
}

/**
 * Resolves the connector download from the published release manifest rather
 * than a hardcoded URL, so a new release is picked up without a frontend
 * deploy. A platform stays unavailable until it is published to the manifest.
 */
export function useConnectorDownload(): ConnectorDownload {
  const { data: manifest, isLoading, isError } = useGetConnectorManifestQuery()
  const platform = detectConnectorPlatform()

  const pkg = platform === 'windows' ? manifest?.windows : platform === 'macos' ? manifest?.macos : undefined
  const zipUrl = platform === 'macos' ? manifest?.macos?.zipUrl : undefined

  return {
    platform,
    url: pkg?.url,
    zipUrl,
    version: manifest?.version,
    isLoading,
    isUnavailable: (!isLoading && !pkg?.url) || isError,
  }
}
